import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, TrendingUp, CheckCircle, DollarSign } from 'lucide-react';

const CHART_COLORS = [
  'hsl(217, 91%, 60%)',  // Azul principal (primary)
  'hsl(199, 89%, 48%)',  // Info/Celeste
  'hsl(217, 91%, 45%)',  // Azul más oscuro
  'hsl(199, 89%, 60%)',  // Celeste más claro
  'hsl(217, 91%, 70%)'   // Azul más claro
];

const ESTADO_COLORS = {
  'Borrador': 'hsl(215, 20%, 65%)',
  'Enviada': 'hsl(217, 91%, 60%)',
  'Adjudicada': 'hsl(158, 64%, 39%)'
};

export const ChartsGrid = ({ vehiculos, ofertas, revendedores, stats }) => {
  let marcasData = [];
  let revendedoresTopCantidad = [];
  let revendedoresTopMonto = [];

  if (vehiculos.length > 0 && ofertas.length > 0 && revendedores.length > 0) {
    // Marcas más adjudicadas
    const normalizeBrand = (brand) => {
      if (!brand) return 'Desconocida';
      let cleaned = brand.trim().replace(/\s+/g, ' ').toLowerCase();
      
      if (['vw', 'volskwagen', 'vokswagen', 'volkswen'].includes(cleaned)) cleaned = 'volkswagen';
      if (cleaned === 'chevy') cleaned = 'chevrolet';
      if (['mb', 'mercedes', 'mercedes benz'].includes(cleaned)) cleaned = 'mercedes-benz';
      if (cleaned === 'bm') cleaned = 'bmw';
      if (cleaned === 'peugot') cleaned = 'peugeot';
      
      return cleaned.split(/[\s-]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const marcasAdjudicadas = {};
    const adjudicados = vehiculos.filter(v => v.estadoPublicacion === 'Adjudicada');
    adjudicados.forEach(v => {
      const marcaNormalizada = normalizeBrand(v.marca);
      marcasAdjudicadas[marcaNormalizada] = (marcasAdjudicadas[marcaNormalizada] || 0) + 1;
    });
    marcasData = Object.entries(marcasAdjudicadas)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    // Revendedores que más compran
    const revendedoresCompras = {};
    const revendedoresMap = {};
    revendedores.forEach(r => { revendedoresMap[r.id] = r; });

    const ofertasAceptadas = ofertas.filter(o => o.estado === 'Aceptada');
    ofertasAceptadas.forEach(o => {
      const rev = revendedoresMap[o.revendedorId];
      if (rev) {
        if (!revendedoresCompras[rev.nombre]) {
          revendedoresCompras[rev.nombre] = { cantidad: 0, monto: 0 };
        }
        revendedoresCompras[rev.nombre].cantidad += 1;
        revendedoresCompras[rev.nombre].monto += o.monto;
      }
    });

    revendedoresTopCantidad = Object.entries(revendedoresCompras)
      .map(([name, data]) => ({ name, value: data.cantidad }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    revendedoresTopMonto = Object.entries(revendedoresCompras)
      .map(([name, data]) => ({ name, value: data.monto }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mt-6 mb-12">
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-12">
      {/* Marcas más adjudicadas - Span 7 columns */}
      {marcasData.length > 0 && (
        <Card className="lg:col-span-7 bg-card border border-border shadow-xl">
          <CardHeader className="pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-foreground">Marcas Más Adjudicadas</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Distribución por marca de vehículos</p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
                <FileText className="h-5 w-5 text-primary" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {marcasData.slice(0, 5).map((item, index) => {
                const maxValue = Math.max(...marcasData.slice(0, 5).map(d => d.value));
                const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
                return (
                  <div key={index} className="group">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg font-bold text-white text-xs border"
                          style={{
                            background: `linear-gradient(135deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`,
                            borderColor: `${CHART_COLORS[index]}40`
                          }}>
                          {index + 1}
                        </div>
                        <span className="font-semibold text-foreground">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground">{percentage.toFixed(0)}%</span>
                        <span className="font-bold text-foreground text-base">{item.value}</span>
                      </div>
                    </div>
                    <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full transition-all duration-700 ease-out group-hover:opacity-90"
                        style={{
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribución de Estados - Circular Chart - Span 5 columns */}
      {vehiculos.length > 0 && (
        <Card className="lg:col-span-5 bg-card border border-border shadow-xl">
          <CardHeader className="pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-foreground">Estado de Publicaciones</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Distribución actual</p>
              </div>
              <div className="p-2 bg-muted rounded-lg">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {(() => {
                if (!stats) return null;
                
                const estadosArray = [
                  { name: 'Activas', value: stats.publicaciones_activas || 0, color: '#3b82f6' }, // text-blue-500
                  { name: 'Vencidas', value: stats.publicaciones_vencidas || 0, color: '#ef4444' }, // text-red-500
                  { name: 'En Espera', value: stats.publicaciones_en_espera || 0, color: '#eab308' }, // text-yellow-500
                  { name: 'Adjudicadas', value: stats.vehiculos_adjudicados || 0, color: '#10b981' }, // text-emerald-500
                  { name: 'No Adjudicadas', value: stats.publicaciones_no_adjudicadas || 0, color: '#64748b' } // text-slate-500
                ].sort((a, b) => b.value - a.value);

                const total = stats.total_publicaciones || estadosArray.reduce((acc, curr) => acc + curr.value, 0);

                return estadosArray.map((item, index) => {
                  const percentage = total > 0 ? (item.value / total) * 100 : 0;
                  const color = item.color;
                  return (
                    <div key={index} className="group">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          <span className="text-sm font-semibold text-foreground">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{percentage.toFixed(1)}%</span>
                          <span className="font-bold text-foreground">{item.value}</span>
                        </div>
                      </div>
                      <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: color
                          }}
                        />
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revendedores que más compran (cantidad) - Span 6 */}
      {revendedoresTopCantidad.length > 0 && (
        <Card className="lg:col-span-6 bg-card border border-border shadow-xl">
          <CardHeader className="pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-foreground">Top Revendedores</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Por cantidad de compras</p>
              </div>
              <div className="p-2 bg-muted rounded-lg">
                <CheckCircle className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {revendedoresTopCantidad.slice(0, 5).map((item, index) => {
                const maxValue = Math.max(...revendedoresTopCantidad.slice(0, 5).map(d => d.value));
                const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
                return (
                  <div key={index} className="group relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-md"
                            style={{
                              background: `linear-gradient(135deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                            }}>
                            {item.value}
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-foreground text-sm">{item.name}</div>
                          <div className="text-xs text-muted-foreground">Compras realizadas</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold text-foreground">{percentage.toFixed(0)}%</div>
                        <div className="text-xs text-muted-foreground">del total</div>
                      </div>
                    </div>
                    <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revendedores top por monto - Span 6 */}
      {revendedoresTopMonto.length > 0 && (
        <Card className="lg:col-span-6 bg-card border border-border shadow-xl">
          <CardHeader className="pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-foreground">Inversión Destacada</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Monto total invertido</p>
              </div>
              <div className="p-2 bg-muted rounded-lg">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {revendedoresTopMonto.slice(0, 5).map((item, index) => {
                const maxValue = Math.max(...revendedoresTopMonto.slice(0, 5).map(d => d.value));
                const percentage = (item.value / maxValue) * 100;
                return (
                  <div key={index} className="group relative">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-white shadow-md text-xs"
                            style={{
                              background: `linear-gradient(135deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                            }}>
                            ${(item.value / 1000).toFixed(0)}K
                          </div>
                        </div>
                        <div>
                          <div className="font-semibold text-foreground text-sm">{item.name}</div>
                          <div className="text-xs text-muted-foreground">${item.value.toLocaleString('en-US')}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-semibold text-foreground">{percentage.toFixed(0)}%</div>
                        <div className="text-xs text-muted-foreground">del total</div>
                      </div>
                    </div>
                    <div className="relative h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${CHART_COLORS[index]} 0%, ${CHART_COLORS[index]} 100%)`
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};
